import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import type { MarketMessage, MarketThreadSummary } from '@/types/market';
import { DEFAULT_AVATAR, timeAgo } from './MarketShared';
import styles from './Market.module.scss';

const POLL_MS = 8000;

export default function MarketMessages() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get('thread');

  const [threads, setThreads] = useState<MarketThreadSummary[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [threadsError, setThreadsError] = useState<string | null>(null);

  const [messages, setMessages] = useState<MarketMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [myPersonId, setMyPersonId] = useState<string | null>(null);

  // Trust & safety
  const [confirmBlock, setConfirmBlock] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const mounted = useRef(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => { if (mounted.current) setToast(null); }, 3000);
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; });
  };

  const loadThreads = useCallback(async () => {
    try {
      const t = await apiService.mktGetThreads();
      if (!mounted.current) return;
      setThreads(t);
      setThreadsError(null);
    } catch (e: any) {
      if (mounted.current) setThreadsError(e?.message || 'Failed to load messages.');
    } finally {
      if (mounted.current) setLoadingThreads(false);
    }
  }, []);

  const loadMessages = useCallback(async (threadId: string, spinner = false) => {
    if (spinner) setLoadingMessages(true);
    try {
      const m = await apiService.mktGetThreadMessages(threadId);
      if (!mounted.current) return;
      setMessages(m);
      setMessagesError(null);
      scrollToBottom();
    } catch (e: any) {
      if (mounted.current) setMessagesError(e?.message || 'Failed to load this conversation.');
    } finally {
      if (mounted.current && spinner) setLoadingMessages(false);
    }
  }, []);

  // Identity for Ably channel
  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const me = await apiService.mktGetMe();
        if (mounted.current) setMyPersonId(me?.id ?? null);
      } catch { /* non-fatal — poll fallback still works */ }
    })();
    loadThreads();
    return () => { mounted.current = false; };
  }, [loadThreads]);

  // Load + poll the active thread
  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    loadMessages(activeId, true);
    const id = setInterval(() => loadMessages(activeId, false), POLL_MS);
    return () => clearInterval(id);
  }, [activeId, loadMessages]);

  // Ably live updates on person:<myPersonId>
  useEffect(() => {
    if (!myPersonId) return;
    const unsub = subscribeToChannel(`person:${myPersonId}`, ['market_message:new'], (_event, data: any) => {
      if (!mounted.current) return;
      const msg: MarketMessage | undefined = data?.message ?? data;
      const threadId: string | undefined = data?.threadId ?? (msg as any)?.threadId;
      // Refresh thread list ordering/last-message
      loadThreads();
      if (activeId && (threadId === activeId || !threadId) && msg?.id) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        scrollToBottom();
      }
    });
    return unsub;
  }, [myPersonId, activeId, loadThreads]);

  const openThread = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('thread', id);
    setSearchParams(next);
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !activeId || sending) return;
    setSending(true);
    try {
      const msg = await apiService.mktSendThreadMessage(activeId, text);
      if (!mounted.current) return;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      setDraft('');
      scrollToBottom();
      loadThreads();
    } catch (e: any) {
      if (mounted.current) {
        setMessagesError(e?.status === 403
          ? 'You can’t message this person.'
          : (e?.message || 'Message failed to send.'));
      }
    } finally {
      if (mounted.current) setSending(false);
    }
  };

  const activeThread = threads.find((t) => t.id === activeId);

  const closeThread = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('thread');
    setSearchParams(next);
  };

  const blockOther = async () => {
    const otherId = activeThread?.otherPerson?.id;
    if (!otherId || blocking) return;
    setBlocking(true);
    try {
      await apiService.mktBlockPerson(otherId);
      if (!mounted.current) return;
      setConfirmBlock(false);
      showToast('Blocked. You won’t receive messages from them anymore.');
      loadThreads();
      closeThread();
    } catch (e: any) {
      if (mounted.current) { setConfirmBlock(false); showToast(e?.message || 'Could not block this person.'); }
    } finally {
      if (mounted.current) setBlocking(false);
    }
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate('/customer/market')}>← Marketplace</button>
        </div>
        <h1 className={styles.title}>Messages</h1>

        <div className={styles.messagesLayout}>
          {/* Threads column */}
          <div>
            {threadsError && <p className={styles.error}>{threadsError}</p>}
            {loadingThreads && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}
            {!loadingThreads && threads.length === 0 && !threadsError && (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>💬</div>
                No conversations yet.
              </div>
            )}
            <div className={styles.threadList}>
              {threads.map((t) => (
                <div key={t.id} className={`${styles.threadRow} ${t.id === activeId ? styles.threadRowActive : ''}`}
                  onClick={() => openThread(t.id)} role="button" tabIndex={0}>
                  <img className={styles.avatar} src={t.otherPerson?.profilePic || DEFAULT_AVATAR} alt={t.otherPerson?.displayName || ''}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                  <div className={styles.threadInfo}>
                    <div className={styles.threadTitle}>{t.otherPerson?.displayName || 'Chat'}</div>
                    {t.listingTitle && <div className={styles.threadLast} style={{ color: 'var(--text-secondary)' }}>{t.listingTitle}</div>}
                    <div className={styles.threadLast}>{t.lastMessage?.text || 'No messages yet'}</div>
                  </div>
                  <span className={styles.threadTime}>{timeAgo(t.lastMessage?.createdAt || t.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Conversation column */}
          <div>
            {!activeId ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>👈</div>
                Select a conversation.
              </div>
            ) : (
              <div className={styles.chatWrap}>
                <div className={styles.chatHead}>
                  <img className={styles.avatar} style={{ width: 36, height: 36 }}
                    src={activeThread?.otherPerson?.profilePic || DEFAULT_AVATAR} alt=""
                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                  <div>
                    <div className={styles.threadTitle}>{activeThread?.otherPerson?.displayName || 'Conversation'}</div>
                    {activeThread?.listingTitle && <div className={styles.threadLast}>{activeThread.listingTitle}</div>}
                  </div>
                  {activeThread?.otherPerson?.id && (
                    <>
                      <span className={styles.spacer} />
                      <button type="button" className={styles.dangerBtn} onClick={() => setConfirmBlock(true)}>🚫 Block</button>
                    </>
                  )}
                </div>
                <div className={styles.chatBody} ref={bodyRef}>
                  {loadingMessages && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}
                  {messagesError && <p className={styles.error}>{messagesError}</p>}
                  {!loadingMessages && messages.length === 0 && !messagesError && (
                    <p className={styles.muted}>Say hello 👋</p>
                  )}
                  {messages.map((m) => {
                    const mine = myPersonId != null && m.senderPersonId === myPersonId;
                    return (
                      <div key={m.id} className={`${styles.bubble} ${mine ? styles.bubbleMine : styles.bubbleTheirs}`}>
                        {m.text}
                        <span className={styles.bubbleTime}>{timeAgo(m.createdAt)}</span>
                      </div>
                    );
                  })}
                </div>
                <form className={styles.composer} onSubmit={send}>
                  <input className={styles.input} placeholder="Type a message…" value={draft} onChange={(e) => setDraft(e.target.value)} />
                  <button type="submit" className={styles.primaryBtn} disabled={sending || !draft.trim()}>
                    {sending ? '…' : 'Send'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Block confirm */}
      {confirmBlock && activeThread?.otherPerson && (
        <div className={styles.sheetOverlay} onClick={() => !blocking && setConfirmBlock(false)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className={styles.sheetTitle}>Block {activeThread.otherPerson.displayName || 'this person'}?</h3>
            <p className={styles.sheetSub}>
              This conversation will close and you won’t receive messages from them. You can unblock them later from their profile.
            </p>
            <div className={styles.sheetActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmBlock(false)} disabled={blocking}>Cancel</button>
              <button type="button" className={styles.dangerBtn} onClick={blockOther} disabled={blocking}>
                {blocking ? 'Blocking…' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={styles.toast}>{toast}</div>}

      <Footer />
    </>
  );
}
